import React, { useEffect, useRef, useState } from 'react';
import { X, Download, FileSpreadsheet, Globe, FolderTree, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';

import { API_BASE, ApiClient } from '../../api/client';

interface ExportToolsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ExportToolsModal: React.FC<ExportToolsModalProps> = ({ isOpen, onClose }) => {
  const [downloading, setDownloading] = useState<string | null>(null);

  const [exportFeedback, setExportFeedback] = useState<string | null>(null);

  // ── statikus csomag: folyamatjelző (a szerver folyamatosan küldi a ZIP-et) ──
  type Progress = {
    phase: 'prepare' | 'download' | 'save' | 'done' | 'error' | 'cancelled';
    received: number; total: number; files: number; startedAt: number; dlStartedAt: number; message?: string; toDisk?: boolean;
  };
  const [progress, setProgress] = useState<Progress | null>(null);
  const [, setTick] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    if (!progress || !['prepare', 'download', 'save'].includes(progress.phase)) return;
    const t = setInterval(() => setTick((x) => x + 1), 1000); // eltelt idő frissítése
    return () => clearInterval(t);
  }, [progress?.phase]);

  if (!isOpen) return null;

  const fmtMB = (b: number) => `${(b / 1048576).toLocaleString('hu-HU', { maximumFractionDigits: b < 10485760 ? 1 : 0 })} MB`;
  const fmtTime = (sec: number) => {
    const s = Math.max(0, Math.round(sec));
    return s >= 60 ? `${Math.floor(s / 60)} perc ${s % 60} mp` : `${s} mp`;
  };

  const downloadStaticWithProgress = async () => {
    const suggested = 'statikus_kozzeteteli_honlap.zip';
    // nagy csomagnál (több száz MB) a fájl közvetlenül a lemezre íródik, ha a böngésző támogatja
    let handle: any = null;
    const picker = (window as any).showSaveFilePicker;
    if (typeof picker === 'function') {
      try {
        handle = await picker({ suggestedName: suggested, types: [{ description: 'ZIP csomag', accept: { 'application/zip': ['.zip'] } }] });
      } catch (e: any) {
        if (e?.name === 'AbortError') return; // a mentési ablakot bezárta
        handle = null;
      }
    }
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const now = Date.now();
    setDownloading('static');
    setProgress({ phase: 'prepare', received: 0, total: 0, files: 0, startedAt: now, dlStartedAt: 0, toDisk: !!handle });
    let writable: any = null;
    try {
      const token = ApiClient.getToken();
      const res = await fetch(`${API_BASE}/export/static-site`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ client_origin: window.location.origin, mode: 'static' }),
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) {
        let msg = 'Export sikertelen';
        try { msg = (await res.json()).error || msg; } catch { /* */ }
        throw new Error(msg);
      }
      const total = Number(res.headers.get('X-Export-Size')) || 0;
      const files = Number(res.headers.get('X-Export-Files')) || 0;
      const cd = res.headers.get('Content-Disposition') || '';
      const fileName = /filename="?([^";]+)"?/i.exec(cd)?.[1] || suggested;
      const dlStart = Date.now();
      setProgress((p) => p && { ...p, phase: 'download', total, files, dlStartedAt: dlStart });
      if (handle) writable = await handle.createWritable();
      const chunks: Uint8Array[] = [];
      const reader = res.body.getReader();
      let received = 0;
      let lastUi = 0;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        received += value.length;
        if (writable) await writable.write(value); else chunks.push(value);
        const t = Date.now();
        if (t - lastUi > 250) { lastUi = t; setProgress((p) => p && { ...p, received }); }
      }
      setProgress((p) => p && { ...p, phase: 'save', received });
      if (writable) {
        await writable.close();
      } else {
        const blob = new Blob(chunks as BlobPart[], { type: 'application/zip' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = fileName;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
      setProgress((p) => p && { ...p, phase: 'done', received, message: `${fmtMB(received)}${files ? `, ${files.toLocaleString('hu-HU')} fájl` : ''} – ${fmtTime((Date.now() - now) / 1000)} alatt${writable ? ' (a kiválasztott helyre mentve)' : ''}` });
    } catch (err: any) {
      try { await writable?.abort?.(); } catch { /* */ }
      if (err?.name === 'AbortError') setProgress((p) => p && { ...p, phase: 'cancelled', message: 'A letöltést megszakítottad.' });
      else setProgress((p) => p && { ...p, phase: 'error', message: err?.message || 'Hiba a letöltés során' });
    } finally {
      abortRef.current = null;
      setDownloading(null);
    }
  };

  const progressPanel = () => {
    if (!progress) return null;
    const p = progress;
    const elapsed = (Date.now() - p.startedAt) / 1000;
    const dlSec = p.dlStartedAt ? (Date.now() - p.dlStartedAt) / 1000 : 0;
    const speed = dlSec > 0.5 ? p.received / dlSec : 0;
    const pct = p.total ? Math.min(99, Math.floor((p.received / p.total) * 100)) : 0;
    const remaining = speed && p.total > p.received ? (p.total - p.received) / speed : 0;
    const active = ['prepare', 'download', 'save'].includes(p.phase);
    return (
      <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-800 space-y-2" role="status" aria-live="polite">
        {active && (
          <div className="h-2 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={100}
            aria-valuenow={p.phase === 'download' && p.total ? pct : undefined} aria-label="Letöltés állapota">
            {p.phase === 'download' && p.total ? (
              <div className="h-full bg-purple-600 transition-[width] duration-300" style={{ width: `${pct}%` }} />
            ) : (
              <div className="h-full w-1/3 bg-purple-500/70 animate-pulse rounded-full" />
            )}
          </div>
        )}
        <div className="flex items-start justify-between gap-3 text-xs">
          <div className="flex items-start gap-1.5 text-slate-700 dark:text-slate-300">
            {active && <Loader2 className="w-3.5 h-3.5 animate-spin text-purple-600 mt-0.5 shrink-0" />}
            {p.phase === 'done' && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />}
            {(p.phase === 'error' || p.phase === 'cancelled') && <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />}
            <span>
              {p.phase === 'prepare' && <>A szerver összeállítja a csomagot (oldalak, bejegyzések, diagramok)… <span className="text-slate-500">{fmtTime(elapsed)}</span></>}
              {p.phase === 'download' && (
                <>
                  <b>{fmtMB(p.received)}</b>{p.total ? <> / ~{fmtMB(p.total)} <b>({pct}%)</b></> : null}
                  {speed ? <> · {fmtMB(speed)}/s</> : null}
                  {remaining ? <> · hátra kb. {fmtTime(remaining)}</> : null}
                  {p.files ? <span className="block text-slate-500">{p.files.toLocaleString('hu-HU')} fájl{p.toDisk ? ' · közvetlenül a lemezre' : ''}</span> : null}
                </>
              )}
              {p.phase === 'save' && <>Mentés…</>}
              {p.phase === 'done' && <>Kész: {p.message}</>}
              {(p.phase === 'error' || p.phase === 'cancelled') && <>{p.message}</>}
            </span>
          </div>
          {active ? (
            <button type="button" onClick={() => abortRef.current?.abort()} className="px-2 py-1 rounded-md text-[11px] font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 cursor-pointer shrink-0">Megszakítás</button>
          ) : (
            <button type="button" onClick={() => setProgress(null)} className="px-2 py-1 rounded-md text-[11px] text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-800 cursor-pointer shrink-0">Elrejtés</button>
          )}
        </div>
      </div>
    );
  };

  const handleKozadatCsvExport = async () => {
    setDownloading('csv');
    setExportFeedback(null);
    try {
      const res = await ApiClient.downloadKozadatCsv();
      const skipMsg = res.skipped_no_attachment > 0 ? ` (${res.skipped_no_attachment} pont kimaradt melléklet nélkül)` : '';
      setExportFeedback(`✅ ${res.row_count} sor kiexportálva a közadat.hu import formátumba${skipMsg}!`);
      setTimeout(() => setExportFeedback(null), 6000);
    } catch (err: any) {
      alert(err.message || 'Hiba a Közadat CSV exportálása során');
    } finally {
      setDownloading(null);
    }
  };

  const triggerDownload = async (path: string, defaultName: string, id: string, bodyData: any = {}) => {
    setDownloading(id);
    try {
      const token = ApiClient.getToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const clientOrigin = typeof window !== 'undefined' ? window.location.origin : '';
      const payload = {
        client_origin: clientOrigin,
        ...bodyData,
      };

      const res = await fetch(`${API_BASE}${path}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error('Export sikertelen');
      }

      const blob = await res.blob();
      // dinamikus csomag: melyik szerverről tölti majd a mellékleteket
      const origin = res.headers.get('X-Export-Origin');
      if (id === 'dynamic' && origin) {
        const local = /\/\/(localhost|127\.0\.0\.1)(:|\/|$)/i.test(origin);
        setExportFeedback(local
          ? `⚠️ A csomag mellékletei a helyi gépről (${origin}) töltődnek – csak ezen a gépen, futó helyi szerverrel működnek. Éles használatra a DEV vagy LIVE felületről exportálj.`
          : `✅ Dinamikus csomag kész. A mellékletek innen töltődnek: ${origin}`);
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = defaultName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      alert(err.message || 'Hiba a letöltés során');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-xs animate-fade-in overflow-y-auto">
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-xl shadow-2xl my-auto max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-5 sm:p-6 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/20 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shadow-xs">
              <Download className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">Adat Export Központ</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Jogszabályi formátumok, archiválási csomagok és mappasablonok letöltése
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-white p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Export Options */}
        <div className="p-5 sm:p-6 space-y-3 overflow-y-auto pr-2 flex-1">

          {/* Mappastruktúra ZIP Sablon */}
          <div className="p-4 bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800/80 rounded-xl flex items-center justify-between gap-4 hover:border-slate-300 dark:hover:border-slate-700 transition-all shadow-xs">
            <div className="flex items-start gap-3">
              <div className="p-2.5 rounded-lg bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-500/20 mt-0.5">
                <FolderTree className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-slate-900 dark:text-white">Mappastruktúra Sablon (ZIP)</h4>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Üres, jogszabályi pontok szerint felépített mapparendszer mintafájlokkal a helyi kitöltéshez
                </p>
              </div>
            </div>
            <button
              onClick={() => triggerDownload('/export/folder-template', 'kozzeteteli_mappastruktura_sablon.zip', 'template')}
              disabled={downloading === 'template'}
              className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-800 dark:text-white border border-slate-300 dark:border-slate-700 rounded-lg text-xs font-semibold transition-colors shrink-0 cursor-pointer disabled:opacity-50"
            >
              {downloading === 'template' ? 'Generálás...' : 'Letöltés'}
            </button>
          </div>

          {/* Kozadat CP1250 CSV */}
          <div className="p-4 bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-300/80 dark:border-emerald-800/80 rounded-xl flex items-center justify-between gap-4 hover:border-emerald-400 dark:hover:border-emerald-700 transition-all shadow-xs">
            <div className="flex items-start gap-3">
              <div className="p-2.5 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700/60 mt-0.5">
                <FileSpreadsheet className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-bold text-slate-900 dark:text-white">közadat.hu CSV Export (CP1250)</h4>
                  <span className="px-1.5 py-0.2 rounded text-[10px] font-bold bg-emerald-100 dark:bg-emerald-900/80 text-emerald-800 dark:text-emerald-200 border border-emerald-300 dark:border-emerald-700">
                    KÖTELEZŐ FORMÁTUM
                  </span>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">
                  A közadat.hu központi portál hivatalos import-formátuma (Windows-1250, pontosvesszővel tagolt). Csatolt fájlok közvetlen URL-jeivel, törvényi pontok szerinti besorolással.
                </p>
              </div>
            </div>
            <button
              onClick={handleKozadatCsvExport}
              disabled={downloading === 'csv'}
              className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-bold transition-colors shrink-0 cursor-pointer disabled:opacity-50 shadow-xs flex items-center gap-1.5"
            >
              <Download className="w-3.5 h-3.5" />
              <span>{downloading === 'csv' ? 'Generálás...' : 'Letöltés'}</span>
            </button>
          </div>

          {exportFeedback && (
            <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 rounded-xl text-xs text-emerald-800 dark:text-emerald-200 font-medium animate-in fade-in flex items-center gap-2">
              <span>{exportFeedback}</span>
            </div>
          )}

          {/* Teljes Excel Munkafüzet */}
          <div className="p-4 bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800/80 rounded-xl flex items-center justify-between gap-4 hover:border-slate-300 dark:hover:border-slate-700 transition-all shadow-xs">
            <div className="flex items-start gap-3">
              <div className="p-2.5 rounded-lg bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-500/20 mt-0.5">
                <FileSpreadsheet className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-slate-900 dark:text-white">Excel Munkafüzet (XLSX)</h4>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Minden közzétételi oldal külön fülön, formázva, belső ellenőrzéshez és archiváláshoz
                </p>
              </div>
            </div>
            <button
              onClick={() => triggerDownload('/export/xlsx', 'kozzeteteli_nyilvantartas.xlsx', 'xlsx')}
              disabled={downloading === 'xlsx'}
              className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-800 dark:text-white border border-slate-300 dark:border-slate-700 rounded-lg text-xs font-semibold transition-colors shrink-0 cursor-pointer disabled:opacity-50"
            >
              {downloading === 'xlsx' ? 'Generálás...' : 'Letöltés'}
            </button>
          </div>

          {/* Statikus Weboldal Csomag (Önálló / Offline) */}
          <div className="p-4 bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800/80 rounded-xl hover:border-slate-300 dark:hover:border-slate-700 transition-all shadow-xs">
           <div className="flex items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="p-2.5 rounded-lg bg-purple-50 dark:bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-500/20 mt-0.5">
                <Globe className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-semibold text-slate-900 dark:text-white">Statikus Weboldal Csomag (ZIP)</h4>
                  <span className="px-1.5 py-0.2 rounded text-[10px] font-bold bg-purple-100 dark:bg-purple-950/80 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
                    ÖNÁLLÓ / OFFLINE
                  </span>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Önálló HTML+CSS archívum 3 nézettel (táblázat, kártyák, lista), beágyazott mellékletekkel. Bármilyen tárhelyen vagy pendrive-on szerver nélkül is működik.
                </p>
              </div>
            </div>
            <button
              onClick={downloadStaticWithProgress}
              disabled={downloading === 'static'}
              className="px-3.5 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-xs font-bold transition-colors shrink-0 cursor-pointer disabled:opacity-50 shadow-xs flex items-center gap-1.5"
            >
              {downloading === 'static' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              <span>{downloading === 'static' ? (progress?.phase === 'download' ? 'Letöltés…' : 'Összeállítás…') : 'Letöltés'}</span>
            </button>
           </div>
           {progressPanel()}
          </div>

          {/* Dinamikus Weboldal Csomag (Központi VFK Integráció) */}
          <div className="p-4 bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800/80 rounded-xl flex items-center justify-between gap-4 hover:border-slate-300 dark:hover:border-slate-700 transition-all shadow-xs">
            <div className="flex items-start gap-3">
              <div className="p-2.5 rounded-lg bg-teal-50 dark:bg-teal-500/10 text-teal-600 dark:text-teal-400 border border-teal-200 dark:border-teal-500/20 mt-0.5">
                <Globe className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-semibold text-slate-900 dark:text-white">Dinamikus Weboldal Csomag (ZIP)</h4>
                  <span className="px-1.5 py-0.2 rounded text-[10px] font-bold bg-teal-100 dark:bg-teal-950/80 text-teal-700 dark:text-teal-300 border border-teal-200 dark:border-teal-800">
                    VFK KÖZPONTI KAPCSOLAT
                  </span>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Weboldalba ágyazható interaktív portál (3 nézet, élő keresés). A dokumentumokat a központi KözzéTér szerverről tölti le biztonságos, maszkolt hivatkozásokkal (rejtett belső tárhely útvonalak).
                </p>
              </div>
            </div>
            <button
              onClick={() => triggerDownload('/export/static-site', 'dinamikus_kozzeteteli_honlap.zip', 'dynamic', { mode: 'dynamic' })}
              disabled={downloading === 'dynamic'}
              className="px-3.5 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-lg text-xs font-bold transition-colors shrink-0 cursor-pointer disabled:opacity-50 shadow-xs flex items-center gap-1.5"
            >
              <Download className="w-3.5 h-3.5" />
              <span>{downloading === 'dynamic' ? 'Generálás...' : 'Letöltés'}</span>
            </button>
          </div>
        </div>

        <div className="p-4 bg-slate-50 dark:bg-slate-950/80 border-t border-slate-200 dark:border-slate-800 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-200 hover:bg-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-sm font-semibold transition-colors cursor-pointer"
          >
            Bezárás
          </button>
        </div>
      </div>
    </div>
  );
};
